import { Route, Routes, Navigate } from "react-router-dom";

import { RequireAuth, RequireRole } from "./components/RequireRole";
import { Layout } from "./components/Layout";
import { Login } from "./pages/Login";
import { AcceptInvite } from "./pages/AcceptInvite";
import { ResetPassword } from "./pages/ResetPassword";
import { ForgotPassword } from "./pages/ForgotPassword";
import { Home } from "./pages/Home";
import { EnterpriseDashboard } from "./pages/dashboards/EnterpriseDashboard";
import { StateDashboard } from "./pages/dashboards/StateDashboard";
import { DistrictDashboard } from "./pages/dashboards/DistrictDashboard";
import { GodownDashboard } from "./pages/dashboards/GodownDashboard";
import { AdminEnterprises } from "./pages/admin/AdminEnterprises";
import { AdminEnterprise } from "./pages/admin/AdminEnterprise";
import { AdminState } from "./pages/admin/AdminState";
import { AdminDistrict } from "./pages/admin/AdminDistrict";
import { StateHeadShell } from "./statehead/StateHeadShell";
import { Overview } from "./statehead/Overview";
import { Districts as SHDistricts } from "./statehead/Districts";
import { DistrictDetail as SHDistrictDetail } from "./statehead/DistrictDetail";
import { GodownDetail as SHGodownDetail } from "./statehead/GodownDetail";
import { Reports as SHReports } from "./statehead/Reports";
import { Profile as SHProfile } from "./statehead/Profile";
import { DistrictHeadShell } from "./districthead/DistrictHeadShell";
import { Overview as DHOverview } from "./districthead/Overview";
import { Godowns as DHGodowns } from "./districthead/Godowns";
import { GodownDetail as DHGodownDetail } from "./districthead/GodownDetail";
import { Reports as DHReports } from "./districthead/Reports";
import { Profile as DHProfile } from "./districthead/Profile";

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/reset/:token" element={<ResetPassword />} />
      <Route path="/forgot" element={<ForgotPassword />} />

      {/* State Head experience — its own chrome (dual-brand top bar +
          navy rail), outside the generic portal Layout. */}
      <Route
        path="/sh"
        element={
          <RequireAuth>
            <RequireRole
              roles={["state_head", "enterprise_admin", "agrilens_super"]}
            >
              <StateHeadShell />
            </RequireRole>
          </RequireAuth>
        }
      >
        <Route index element={<Overview />} />
        <Route path="districts" element={<SHDistricts />} />
        <Route path="district/:id" element={<SHDistrictDetail />} />
        <Route path="godown/:id" element={<SHGodownDetail />} />
        <Route path="reports" element={<SHReports />} />
        <Route path="profile" element={<SHProfile />} />
      </Route>

      {/* District Head experience — the same chrome as State Head, scoped
          one level down to a single district's godowns. */}
      <Route
        path="/dh"
        element={
          <RequireAuth>
            <RequireRole
              roles={[
                "district_head", "state_head",
                "enterprise_admin", "agrilens_super",
              ]}
            >
              <DistrictHeadShell />
            </RequireRole>
          </RequireAuth>
        }
      >
        <Route index element={<DHOverview />} />
        <Route path="godowns" element={<DHGodowns />} />
        <Route path="godown/:id" element={<DHGodownDetail />} />
        <Route path="reports" element={<DHReports />} />
        <Route path="profile" element={<DHProfile />} />
      </Route>

      {/* Authenticated routes */}
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Home />} />

        {/* Scope-typed dashboards. Access enforcement lives in the
            backend rollup endpoints; the RequireRole guard here is
            a cheap client-side pre-check that keeps the UI clean.
            The server always has the final say. */}
        <Route
          path="/enterprise/:id"
          element={
            <RequireRole roles={["agrilens_super", "enterprise_admin"]}>
              <EnterpriseDashboard />
            </RequireRole>
          }
        />
        <Route
          path="/state/:id"
          element={
            <RequireRole
              roles={["agrilens_super", "enterprise_admin", "state_head"]}
            >
              <StateDashboard />
            </RequireRole>
          }
        />
        <Route
          path="/district/:id"
          element={
            <RequireRole
              roles={[
                "agrilens_super", "enterprise_admin",
                "state_head", "district_head",
              ]}
            >
              <DistrictDashboard />
            </RequireRole>
          }
        />
        <Route
          path="/godown/:id"
          element={
            <RequireRole
              roles={[
                "agrilens_super", "enterprise_admin", "state_head",
                "district_head", "godown_qc_agent",
              ]}
            >
              <GodownDashboard />
            </RequireRole>
          }
        />

        {/* Admin: build the tenant hierarchy + send invites down the
            cascade. Each level's RequireRole mirrors the backend
            role gate for the matching create/invite endpoints —
            the server re-checks scope on every call, so these are
            just UX guards. */}
        <Route
          path="/admin/enterprises"
          element={
            <RequireRole roles={["agrilens_super"]}>
              <AdminEnterprises />
            </RequireRole>
          }
        />
        <Route
          path="/admin/enterprise/:id"
          element={
            <RequireRole roles={["agrilens_super", "enterprise_admin"]}>
              <AdminEnterprise />
            </RequireRole>
          }
        />
        <Route
          path="/admin/state/:id"
          element={
            <RequireRole
              roles={["agrilens_super", "enterprise_admin", "state_head"]}
            >
              <AdminState />
            </RequireRole>
          }
        />
        <Route
          path="/admin/district/:id"
          element={
            <RequireRole
              roles={[
                "agrilens_super", "enterprise_admin",
                "state_head", "district_head",
              ]}
            >
              <AdminDistrict />
            </RequireRole>
          }
        />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
